import type { QueryClient, QueryKey } from "@tanstack/react-query";

/**
 * How a page declares the data it needs, once, for every caller that wants to fetch it early.
 *
 * A page exports `pageQueries` next to its own `useQuery` calls. Two things then consume it:
 *   - the hover prefetch (src/navPrefetch.ts), which fires them off and forgets them;
 *   - a route loader (src/router.tsx), which awaits them so the navigation commits with the data
 *     already in cache.
 *
 * Both go through the same descriptor deliberately. A prefetch or a prime keyed differently than
 * the component's read is worse than doing nothing — it spends a request and still misses the
 * cache — so the key exists in exactly one place: the page.
 */

/** What a page may need that it cannot get from a hook, because neither caller runs in React. */
export interface PageDataContext {
  /** The signed-in user's id, for queries scoped to them. */
  userId?: string;
  /**
   * The signed-in user's roles, so a page can skip a prefetch its endpoint would refuse (a 403 here
   * still opens the global error dialog). Absent when identity has not loaded yet — then the page's
   * own guard decides.
   */
  roles?: string[];
}

export interface PageQuery {
  queryKey: QueryKey;
  queryFn: () => Promise<unknown>;
}

export type PageQueries = (ctx: PageDataContext) => PageQuery[];

/** The shape of a page module that opts in. Most pages export nothing and are simply skipped. */
export interface PageDataModule {
  pageQueries?: PageQueries;
}

function queriesFor(mod: unknown, ctx: PageDataContext): PageQuery[] {
  return (mod as PageDataModule)?.pageQueries?.(ctx) ?? [];
}

/** Fire a page's queries without waiting — for hover, where nothing depends on the result. */
export function prefetchPageData(
  client: QueryClient,
  mod: unknown,
  ctx: PageDataContext = {},
): void {
  queriesFor(mod, ctx).forEach((query) => {
    client.prefetchQuery(query);
  });
}

/**
 * Resolve a page's queries — for a loader, where the navigation waits on them.
 *
 * `ensureQueryData` returns a cached entry when there is one, so a revisit inside the client's
 * staleTime resolves without a request and the navigation is instant.
 */
export async function loadPageData(
  client: QueryClient,
  mod: unknown,
  ctx: PageDataContext = {},
): Promise<void> {
  await Promise.all(
    queriesFor(mod, ctx).map((query) => client.ensureQueryData(query)),
  );
}
